import { useState, useEffect } from 'react';
import { Brain, Clock, Hash, Target, RefreshCw, AlertCircle } from 'lucide-react';
import { Badge } from './ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * OptimizerStatusPanel - Displays optimizer telemetry for a truck
 * 
 * This component is TELEMETRY ONLY:
 * - Does NOT trigger optimization
 * - Does NOT block page render
 * - Fetches data asynchronously on mount
 * - Single fetch on load (no polling)
 */
export default function OptimizerStatusPanel({ truckId }) {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        // Abort controller for cleanup
        const controller = new AbortController();

        const fetchOptimizerStatus = async () => {
            if (!truckId) {
                setLoading(false);
                return;
            }

            try {
                const response = await fetch(
                    `${API_URL}/api/optimizer/truck/${truckId}/trigger-status`,
                    {
                        credentials: 'include',
                        signal: controller.signal
                    }
                );

                if (!response.ok) {
                    if (response.status === 401) {
                        // Not authenticated - graceful handling
                        setError('Not authenticated');
                    } else {
                        setError('Failed to fetch optimizer status');
                    }
                    setLoading(false);
                    return;
                }

                const data = await response.json();
                setStatus(data);
                setError(null);
            } catch (err) {
                if (err.name === 'AbortError') {
                    // Request was aborted - ignore
                    return;
                }
                setError('Optimizer status unavailable');
            } finally {
                setLoading(false);
            }
        };

        // Async fetch - does NOT block render
        fetchOptimizerStatus();

        // Cleanup on unmount
        return () => controller.abort();
    }, [truckId]);

    /**
     * Calculate freshness status based on last run timestamp
     * - Fresh: < 5 minutes
     * - Aging: 5-30 minutes
     * - Stale: > 30 minutes or no data
     */
    const getFreshnessStatus = () => {
        if (!status?.has_run || !status?.last_timestamp) {
            return { status: 'stale', label: 'Stale', color: 'bg-red-500/20 text-red-400' };
        }

        const lastRun = new Date(status.last_timestamp);
        const now = new Date();
        const diffMinutes = (now - lastRun) / (1000 * 60);

        if (diffMinutes < 5) {
            return { status: 'fresh', label: 'Fresh', color: 'bg-green-500/20 text-green-400' };
        } else if (diffMinutes < 30) {
            return { status: 'aging', label: 'Aging', color: 'bg-yellow-500/20 text-yellow-400' };
        } else {
            return { status: 'stale', label: 'Stale', color: 'bg-red-500/20 text-red-400' };
        }
    };

    /**
     * Format relative time (e.g., "2 min ago", "1 hour ago")
     */
    const formatRelativeTime = (timestamp) => {
        if (!timestamp) return 'Never';

        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now - date;
        const diffSec = Math.floor(diffMs / 1000);
        const diffMin = Math.floor(diffSec / 60);
        const diffHour = Math.floor(diffMin / 60);
        const diffDay = Math.floor(diffHour / 24);

        if (diffSec < 60) return 'Just now';
        if (diffMin < 60) return `${diffMin} min ago`;
        if (diffHour < 24) return `${diffHour} hour${diffHour > 1 ? 's' : ''} ago`;
        return `${diffDay} day${diffDay > 1 ? 's' : ''} ago`;
    };

    /**
     * Format timestamp for display
     */
    const formatDateTime = (timestamp) => {
        if (!timestamp) return 'N/A';
        try {
            const date = new Date(timestamp);
            return date.toLocaleString(undefined, {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return 'Invalid date';
        }
    };

    // Loading state - minimal to not block UI
    if (loading) {
        return (
            <div 
                className="flex items-center gap-2 text-xs text-zinc-500"
                data-testid="optimizer-status-loading"
            >
                <RefreshCw className="w-3 h-3 animate-spin" />
                <span>Loading optimizer status...</span>
            </div>
        );
    }

    // Error state - graceful display
    if (error) {
        return (
            <div 
                className="flex items-center gap-2 text-xs text-zinc-500"
                data-testid="optimizer-status-error"
            >
                <AlertCircle className="w-3 h-3" />
                <span>{error}</span>
            </div>
        );
    }

    // No data state
    if (!status?.has_run) {
        return (
            <div 
                className="bg-zinc-800/30 rounded-lg p-3"
                data-testid="optimizer-status-no-data"
            >
                <div className="flex items-center gap-2 mb-2">
                    <Brain className="w-4 h-4 text-purple-400" />
                    <span className="text-xs text-zinc-400 font-medium">Optimizer Status</span>
                    <Badge 
                        className="bg-red-500/20 text-red-400 text-xs ml-auto"
                        data-testid="optimizer-status-badge"
                    >
                        Stale
                    </Badge>
                </div>
                <p className="text-xs text-zinc-500">No optimization data yet</p>
            </div>
        );
    }

    const freshness = getFreshnessStatus();

    return (
        <div 
            className="bg-zinc-800/30 rounded-lg p-3"
            data-testid="optimizer-status-panel"
        >
            {/* Header with status badge */}
            <div className="flex items-center gap-2 mb-3">
                <Brain className="w-4 h-4 text-purple-400" />
                <span className="text-xs text-zinc-400 font-medium">Optimizer Status</span>
                <Badge 
                    className={`${freshness.color} text-xs ml-auto`}
                    data-testid="optimizer-status-badge"
                >
                    {freshness.label}
                </Badge>
            </div>

            {/* Status grid */}
            <div className="grid grid-cols-2 gap-2 text-xs">
                {/* Last Run */}
                <div 
                    className="flex items-center gap-1.5"
                    data-testid="optimizer-last-run"
                >
                    <Clock className="w-3 h-3 text-zinc-500" />
                    <div>
                        <span className="text-zinc-500">Last Run: </span>
                        <span className="text-zinc-300" title={formatDateTime(status.last_timestamp)}>
                            {formatRelativeTime(status.last_timestamp)}
                        </span>
                    </div>
                </div>

                {/* Chains Found */}
                <div 
                    className="flex items-center gap-1.5"
                    data-testid="optimizer-chains-found"
                >
                    <Hash className="w-3 h-3 text-zinc-500" />
                    <div>
                        <span className="text-zinc-500">Chains: </span>
                        <span className="text-zinc-300 font-mono">
                            {status.chains_found ?? status.legal_chains_found ?? 0}
                        </span>
                    </div>
                </div>

                {/* Best Score */}
                <div 
                    className="flex items-center gap-1.5"
                    data-testid="optimizer-best-score"
                >
                    <Target className="w-3 h-3 text-zinc-500" />
                    <div>
                        <span className="text-zinc-500">Best Score: </span>
                        <span className="text-zinc-300 font-mono">
                            {status.best_score != null ? status.best_score.toFixed(2) : 'N/A'}
                        </span>
                    </div>
                </div>

                {/* Resets in Best Chain */}
                <div 
                    className="flex items-center gap-1.5"
                    data-testid="optimizer-resets"
                >
                    <RefreshCw className="w-3 h-3 text-zinc-500" />
                    <div>
                        <span className="text-zinc-500">Resets: </span>
                        <span className="text-zinc-300 font-mono">
                            {status.resets_in_best_chain ?? 'N/A'}
                        </span>
                    </div>
                </div>
            </div>

            {/* Trigger info (if available) */}
            {status.last_trigger_type && (
                <div className="mt-2 pt-2 border-t border-zinc-700/50">
                    <span className="text-xs text-zinc-500">
                        Triggered by: <span className="text-zinc-400">{status.last_trigger_type.replace(/_/g, ' ')}</span>
                    </span>
                </div>
            )}

            {/* Location fallback message (Phase 6.5) */}
            {status.location_fallback_used && (
                <div 
                    className="mt-2 pt-2 border-t border-zinc-700/50 flex items-center gap-1.5"
                    data-testid="optimizer-fallback-message"
                >
                    <AlertCircle className="w-3 h-3 text-yellow-500" />
                    <span className="text-xs text-yellow-500">
                        {status.location_fallback_reason || "Using home base as current location"}
                    </span>
                </div>
            )}
        </div>
    );
}
